/**
 * Validation utilities for PhysicalConnectionStep
 * Handles validation of asset connections, subtypes, and powertrain configurations
 * Based on allowed driving relationships defined in MVP requirements
 */

import { ALLOWED_ASSET_TYPES } from '../constants';

/**
 * Validation error types
 */
const VALIDATION_ERROR_TYPES = {
    ALREADY_CONNECTED: 'ALREADY_CONNECTED',
    MISSING_OUTPUT_RATIO: 'MISSING_OUTPUT_RATIO',
    INVALID_CONNECTION_ORDER: 'INVALID_CONNECTION_ORDER',
    INVALID_DRIVING_CONNECTION: 'INVALID_DRIVING_CONNECTION',
    MISSING_DRIVE_OR_MOTOR: 'MISSING_DRIVE_OR_MOTOR',
    INSUFFICIENT_ASSETS: 'INSUFFICIENT_ASSETS',
    DRIVE_NOT_FIRST: 'DRIVE_NOT_FIRST',
    MULTIPLE_DRIVES: 'MULTIPLE_DRIVES',
    MOTOR_NOT_FIRST: 'MOTOR_NOT_FIRST',
    MULTIPLE_MOTORS: 'MULTIPLE_MOTORS',
    MULTIPLE_GEARBOXES: 'MULTIPLE_GEARBOXES'
};

/**
 * Define allowed driving relationships based on MVP requirements
 * Key: Asset type that CAN BE DRIVEN
 * Value: Array of asset types that CAN DRIVE it
 *
 * Relationships:
 * - Drive: no driving object in MVP
 * - Motor: driven by Drive (INU)
 * - GM Gearbox: driven by Motor, GM Bearing
 * - GM Bearing: driven by Motor, GM Gearbox
 * - GM Pump: driven by Motor, GM Gearbox, GM Bearing
 * - GM Fan: driven by Motor, GM Gearbox, GM Bearing
 * - GM (no sub type): driven by Motor, GM Gearbox, GM Bearing
 */
const ALLOWED_DRIVING_RELATIONSHIPS = {
    // Drive has no driving object in MVP
    'drive': [],

    // Motor is driven by Drive (INU)
    'motor': ['drive'],

    // GM Gearbox is driven by Motor or GM Bearing
    'gearbox': ['motor', 'bearing'],

    // GM Bearing is driven by Motor or GM Gearbox
    'bearing': ['motor', 'gearbox'],

    // GM Pump is driven by Motor, GM Gearbox, or GM Bearing
    'pump': ['motor', 'gearbox', 'bearing'],

    // DE Pump follows the same driving rules as GM Pump
    'de_pump': ['motor', 'gearbox', 'bearing'],

    // GM Fan is driven by Motor, GM Gearbox, or GM Bearing
    'fan': ['motor', 'gearbox', 'bearing'],

    // Generic Machine (no subtype) is driven by Motor, GM Gearbox, or GM Bearing
    'genericmachine': ['motor', 'gearbox', 'bearing']
};

/**
 * Get the normalized asset type for validation
 * Handles GenericMachine subtypes and standard types
 * @param {Object} asset - Asset object or asset details
 * @returns {string} Normalized asset type key for validation
 */
const getNormalizedAssetType = (asset) => {
    // Handle both asset object formats (from physical connection and from API)
    const baseType = (asset?.assetType || asset?.type || '').toLowerCase();

    // For GenericMachine, use the subtype if available
    if (baseType === 'genericmachine') {
        const subType = (asset?.subType || asset?.selectedSubType || asset?.assetSubType || '').toLowerCase();
        if (subType) {
            // Map common subtype names to validation keys
            if (subType.includes('pump')) {
                return 'pump';
            }
            if (subType.includes('fan')) {
                return 'fan';
            }
            if (subType.includes('gearbox') || subType.includes('gear')) {
                return 'gearbox';
            }
            if (subType.includes('bearing')) {
                return 'bearing';
            }
        }
        // GenericMachine without subtype
        return 'genericmachine';
    }

    // Handle standard types
    if (baseType.includes('gearbox') || baseType.includes('gearing')) {
        return 'gearbox';
    }
    if (baseType.includes('bearing')) {
        return 'bearing';
    }

    return baseType;
};

/**
 * Get validation message using translation function
 * @param {Function} translate - Translation function
 * @param {string} errorType - Error type
 * @returns {string} Translated message
 */
const getValidationMessage = (translate, errorType) => {
    const messageKeys = {
        [VALIDATION_ERROR_TYPES.ALREADY_CONNECTED]: 'ABB.Powertrain.Frontend.ptToolsPowertrainWizard_Validation_AssetAlreadyConnected',
        [VALIDATION_ERROR_TYPES.MISSING_OUTPUT_RATIO]: 'ABB.Powertrain.Frontend.ptToolsPowertrainWizard_Validation_MissingOutputRatio',
        [VALIDATION_ERROR_TYPES.INVALID_CONNECTION_ORDER]: 'ABB.Powertrain.Frontend.ptToolsPowertrainWizard_Validation_InvalidConnectionOrder',
        [VALIDATION_ERROR_TYPES.INVALID_DRIVING_CONNECTION]: 'ABB.Powertrain.Frontend.ptToolsPowertrainWizard_Validation_InvalidDrivingConnection',
        [VALIDATION_ERROR_TYPES.MISSING_DRIVE_OR_MOTOR]: 'ABB.Powertrain.Frontend.ptToolsPowertrainWizard_Validation_MissingDriveOrMotor',
        [VALIDATION_ERROR_TYPES.INSUFFICIENT_ASSETS]: 'ABB.Powertrain.Frontend.ptToolsPowertrainWizard_Validation_InsufficientAssets'
    };

    return translate(messageKeys[errorType] || errorType);
};

/**
 * Returns true when the output ratio value is present and a positive number.
 * @param {string|number|undefined} outputRatio
 * @returns {boolean}
 */
const isOutputRatioValid = (outputRatio) => {
    if (!outputRatio || outputRatio === '') { return false; }
    const ratio = Number.parseFloat(outputRatio);
    return !Number.isNaN(ratio) && ratio > 0;
};

/**
 * Validates a single asset based on its details
 * @param {Object} asset - Asset from physical connection
 * @param {Object} assetDetails - Detailed asset information from API
 * @param {Object} outputRatios - Output ratios for assets that require them
 * @param {Function} translate - Translation function
 * @returns {Object} Validation result
 */
const validateAsset = (asset, assetDetails, outputRatios = {}, translate) => {
    const errors = [];
    const warnings = [];

    // Unmonitored gearboxes have no API details – validate output ratio directly
    if (asset?.isUnmonitoredGearbox) {
        if (!isOutputRatioValid(outputRatios[asset?.assetId])) {
            errors.push({
                type: VALIDATION_ERROR_TYPES.MISSING_OUTPUT_RATIO,
                message: getValidationMessage(translate, VALIDATION_ERROR_TYPES.MISSING_OUTPUT_RATIO),
                severity: 'error',
                assetId: asset.assetId,
                requiresInput: true
            });
        }
        return {
            isValid: errors?.length === 0,
            errors,
            warnings,
            requiresOutputRatio: true,
            outputRatio: outputRatios[asset?.assetId] || ''
        };
    }

    if (!assetDetails) {
        warnings.push(translate('ABB.Powertrain.Frontend.ptToolsPowertrainWizard_Validation_AssetDetailsNotLoaded'));
        return { isValid: false, errors, warnings };
    }

    // Check if asset is already connected to another powertrain
    // Show ERROR if connected to a DIFFERENT powertrain
    // This displays error banner and prevents user from proceeding
    if (asset?.isConnectedToPowertrain && !asset?.isConnectedToSamePowertrain) {
        errors.push({
            type: VALIDATION_ERROR_TYPES.ALREADY_CONNECTED,
            message: getValidationMessage(translate, VALIDATION_ERROR_TYPES.ALREADY_CONNECTED),
            severity: 'error',
            assetId: asset.assetId,
            additionalInfo: assetDetails.powertrain ?
                `${translate('ABB.Powertrain.Frontend.ptToolsPowertrainWizard_Validation_ConnectedTo')} ${assetDetails.powertrain.name}` :
                null
        });
    }

    // Check if output speed ratio is required but missing
    if (assetDetails?.subTypeConfiguration?.hasOutputSpeedRatio) {
        if (!isOutputRatioValid(outputRatios[asset?.assetId])) {
            errors.push({
                type: VALIDATION_ERROR_TYPES.MISSING_OUTPUT_RATIO,
                message: getValidationMessage(translate, VALIDATION_ERROR_TYPES.MISSING_OUTPUT_RATIO),
                severity: 'error',
                assetId: asset.assetId,
                requiresInput: true
            });
        }
    }

    return {
        isValid: errors.length === 0,
        errors,
        warnings,
        requiresOutputRatio: assetDetails.subTypeConfiguration?.hasOutputSpeedRatio || false,
        outputRatio: outputRatios[asset.assetId] || ''
    };
};

/**
 * Validates if an asset can be driven by another asset
 * Based on ALLOWED_DRIVING_RELATIONSHIPS
 * @param {Object} drivenAsset - Asset that should be driven
 * @param {Object} drivingAsset - Asset that should drive
 * @returns {boolean} True if the connection is valid
 */
const canBeDrivenBy = (drivenAsset, drivingAsset) => {
    const drivenType = getNormalizedAssetType(drivenAsset);
    const drivingType = getNormalizedAssetType(drivingAsset);

    // Get allowed drivers for the driven asset
    const allowedDrivers = ALLOWED_DRIVING_RELATIONSHIPS[drivenType] || [];

    const isValid = allowedDrivers.includes(drivingType);

    return isValid;
};

/**
 * Validates that Drive assets are positioned correctly
 * Drive must be at the start of the chain and there should be only one
 * @param {Array} physicalConnection - Array of assets in physical connection
 * @param {Function} translate - Translation function
 * @returns {Array} Array of Drive position validation errors
 */
const validateDrivePosition = (physicalConnection, translate) => {
    const errors = [];

    // Find all Drive assets
    const driveAssets = physicalConnection?.filter(asset =>
        getNormalizedAssetType(asset) === 'drive'
    );

    // If no Drive assets, this is valid (optional in physical connection)
    if (driveAssets?.length === 0) {
        return errors;
    }

    // Check if there are multiple Drive assets
    if (driveAssets?.length > 1) {
        errors.push({
            type: VALIDATION_ERROR_TYPES.MULTIPLE_DRIVES,
            message: translate('ABB.Powertrain.Frontend.ptToolsPowertrainWizard_PhysicalConnection_MultipleDrives') ||
                'Only one Drive asset is allowed in the physical connection',
            severity: 'error'
        });
        // Mark all drive assets as invalid
        driveAssets?.forEach(asset => {
            if (!errors.some(e => e.assetId === asset?.assetId)) {
                errors.push({
                    type: VALIDATION_ERROR_TYPES.MULTIPLE_DRIVES,
                    message: translate('ABB.Powertrain.Frontend.ptToolsPowertrainWizard_PhysicalConnection_MultipleDrives') ||
                        'Only one Drive asset is allowed in the physical connection',
                    severity: 'error',
                    assetId: asset?.assetId || asset?.id
                });
            }
        });
    }

    // Drive must be at position 0
    driveAssets?.forEach(driveAsset => {
        const driveIndex = physicalConnection?.findIndex(a => a.id === driveAsset?.id);
        if (driveIndex !== 0) {
            errors.push({
                type: VALIDATION_ERROR_TYPES.DRIVE_NOT_FIRST,
                message: translate('ABB.Powertrain.Frontend.ptToolsPowertrainWizard_PhysicalConnection_DriveNotFirst') ||
                    'Drive asset must be at the start of the physical connection chain',
                severity: 'error',
                assetId: driveAsset?.assetId || driveAsset?.id
            });
        }
    });

    return errors;
};

/**
 * Validates that Motor assets are positioned correctly
 * When there's no Drive, Motor must be at the start of the chain and there should be only one
 * @param {Array} physicalConnection - Array of assets in physical connection
 * @param {Function} translate - Translation function
 * @returns {Array} Array of Motor position validation errors
 */
const validateMotorPosition = (physicalConnection, translate) => {
    const errors = [];

    // Find all Motor assets
    const motorAssets = physicalConnection?.filter(asset =>
        getNormalizedAssetType(asset) === 'motor'
    );

    // If no Motor assets, this is valid
    if (motorAssets?.length === 0) {
        return errors;
    }

    // Check if there are multiple Motor assets
    if (motorAssets?.length > 1) {
        errors.push({
            type: VALIDATION_ERROR_TYPES.MULTIPLE_MOTORS,
            message: translate('ABB.Powertrain.Frontend.ptToolsPowertrainWizard_PhysicalConnection_MultipleMotors') ||
                'Only one Motor asset is allowed in the physical connection',
            severity: 'error'
        });
        // Mark all motor assets as invalid
        motorAssets?.forEach(asset => {
            errors.push({
                type: VALIDATION_ERROR_TYPES.MULTIPLE_MOTORS,
                message: translate('ABB.Powertrain.Frontend.ptToolsPowertrainWizard_PhysicalConnection_MultipleMotors') ||
                    'Only one Motor asset is allowed in the physical connection',
                severity: 'error',
                assetId: asset?.assetId || asset?.id
            });
        });
    }

    // Check if there's no Drive in the connection
    const hasDrive = physicalConnection?.some(asset =>
        getNormalizedAssetType(asset) === 'drive'
    );

    // If no Drive, Motor must be at position 0
    if (!hasDrive) {
        motorAssets?.forEach(motorAsset => {
            const motorIndex = physicalConnection?.findIndex(a => a.id === motorAsset.id);
            if (motorIndex !== 0) {
                errors.push({
                    type: VALIDATION_ERROR_TYPES.MOTOR_NOT_FIRST,
                    message: translate('ABB.Powertrain.Frontend.ptToolsPowertrainWizard_PhysicalConnection_MotorNotFirst') ||
                        'Motor should be at the start of the physical connection',
                    severity: 'error',
                    assetId: motorAsset?.assetId || motorAsset?.id
                });
            }
        });
    }

    return errors;
};

/**
 * Validates that there is only one Gearbox asset in the connection
 * @param {Array} physicalConnection - Array of assets in physical connection
 * @param {Function} translate - Translation function
 * @returns {Array} Array of Gearbox validation errors
 */
const validateGearboxCount = (physicalConnection, translate) => {
    const errors = [];

    // Find all Gearbox assets
    const gearboxAssets = physicalConnection?.filter(asset =>
        getNormalizedAssetType(asset) === 'gearbox'
    );

    // If no Gearbox assets or only one, this is valid
    if (gearboxAssets?.length <= 1) {
        return errors;
    }

    // Check if there are multiple Gearbox assets
    if (gearboxAssets?.length > 1) {
        errors.push({
            type: VALIDATION_ERROR_TYPES.MULTIPLE_GEARBOXES,
            message: translate('ABB.Powertrain.Frontend.ptToolsPowertrainWizard_PhysicalConnection_MultipleGearboxes') ||
                'Only one Gearbox asset is allowed in the physical connection',
            severity: 'error'
        });
        // Mark all gearbox assets as invalid
        gearboxAssets?.forEach(asset => {
            errors.push({
                type: VALIDATION_ERROR_TYPES.MULTIPLE_GEARBOXES,
                message: translate('ABB.Powertrain.Frontend.ptToolsPowertrainWizard_PhysicalConnection_MultipleGearboxes') ||
                    'Only one Gearbox asset is allowed in the physical connection',
                severity: 'error',
                assetId: asset?.assetId || asset?.id
            });
        });
    }

    return errors;
};

/**
 * Validates driving connections between assets in physical connection
 * Uses ALLOWED_DRIVING_RELATIONSHIPS to determine valid connections
 * @param {Array} physicalConnection - Array of assets in physical connection order
 * @param {Array} assetDetailsArray - Array of detailed asset information
 * @param {Function} translate - Translation function
 * @returns {Array} Array of driving connection validation errors
 */
const validateDrivingConnections = (physicalConnection, assetDetailsArray, translate) => {
    const drivingErrors = [];

    // First, validate Drive position
    const driveErrors = validateDrivePosition(physicalConnection, translate);
    drivingErrors.push(...driveErrors);

    // Validate Motor position
    const motorErrors = validateMotorPosition(physicalConnection, translate);
    drivingErrors.push(...motorErrors);

    // Validate Gearbox count
    const gearboxErrors = validateGearboxCount(physicalConnection, translate);
    drivingErrors.push(...gearboxErrors);

    // Check each consecutive pair in the chain (starting from index 1)
    for (let i = 1; i < physicalConnection.length; i++) {
        const currentAsset = physicalConnection[i];
        const drivingAsset = physicalConnection[i - 1]; // Previous asset in the chain drives current

        // Get asset details for better error messages
        const currentAssetDetails = assetDetailsArray.find(detail =>
            detail.assetId?.toString() === currentAsset.assetId?.toString()
        );
        const drivingAssetDetails = assetDetailsArray.find(detail =>
            detail.assetId?.toString() === drivingAsset.assetId?.toString()
        );

        // Validate the connection using the simplified rules
        if (!canBeDrivenBy(currentAsset, drivingAsset)) {
            const currentType = getNormalizedAssetType(currentAsset);
            const drivingType = getNormalizedAssetType(drivingAsset);
            const allowedDrivers = ALLOWED_DRIVING_RELATIONSHIPS[currentType] || [];

            // Build descriptive labels
            const currentLabel = currentAssetDetails
                ? `${currentAssetDetails.assetType}${currentAssetDetails.assetSubType ? ` (${currentAssetDetails.assetSubType})` : ''}`
                : currentAsset.name || currentType;
            const drivingLabel = drivingAssetDetails
                ? `${drivingAssetDetails.assetType}${drivingAssetDetails.assetSubType ? ` (${drivingAssetDetails.assetSubType})` : ''}`
                : drivingAsset.name || drivingType;

            const cannotBeDrivenByText = translate('ABB.Powertrain.Frontend.ptToolsPowertrainWizard_Validation_CannotBeDrivenBy') || 'cannot be driven by';
            const allowedDriversText = translate('ABB.Powertrain.Frontend.ptToolsPowertrainWizard_Validation_AllowedDrivers') || 'Allowed drivers';

            drivingErrors.push({
                type: VALIDATION_ERROR_TYPES.INVALID_DRIVING_CONNECTION,
                message: `${currentLabel} ${cannotBeDrivenByText} ${drivingLabel}. ${allowedDriversText}: ${drivingType === ALLOWED_ASSET_TYPES[1] ? ALLOWED_DRIVING_RELATIONSHIPS[currentType]?.[0] : allowedDrivers?.join(', ') || 'none'}`,
                severity: 'error',
                assetId: currentAsset.assetId || currentAsset.id,
                drivingAssetId: drivingAsset.assetId || drivingAsset.id,
                supportedDrivingTypes: allowedDrivers
            });
        }
    }

    return drivingErrors;
};

/**
 * Validates the entire physical connection configuration
 * @param {Array} physicalConnection - Array of assets in physical connection
 * @param {Array} assetDetailsArray - Array of detailed asset information
 * @param {Object} outputRatios - Output ratios for assets
 * @param {Function} translate - Translation function
 * @returns {Object} Overall validation result
 */
export const validatePhysicalConnection = (physicalConnection, assetDetailsArray, outputRatios = {}, translate) => {
    const validationResults = [];
    let hasValidationErrors = false;
    const globalErrors = [];

    // Check if we have minimum required assets
    if (physicalConnection?.length < 2) {
        globalErrors.push({
            type: VALIDATION_ERROR_TYPES.INSUFFICIENT_ASSETS,
            message: getValidationMessage(translate, VALIDATION_ERROR_TYPES.INSUFFICIENT_ASSETS),
            severity: 'warning'
        });
    }

    // Check if we have at least one drive or motor
    const hasDriveOrMotor = physicalConnection?.some(asset => {
        const assetDetails = assetDetailsArray?.find(detail => detail?.assetId?.toString() === asset?.assetId?.toString());
        return assetDetails && (assetDetails.assetType === 'Drive' || assetDetails.assetType === 'Motor');
    });

    if (physicalConnection?.length > 0 && !hasDriveOrMotor) {
        globalErrors.push({
            type: VALIDATION_ERROR_TYPES.MISSING_DRIVE_OR_MOTOR,
            message: getValidationMessage(translate, VALIDATION_ERROR_TYPES.MISSING_DRIVE_OR_MOTOR),
            severity: 'error'
        });
        hasValidationErrors = true;
    }

    // Validate driving connections between assets
    const drivingConnectionErrors = validateDrivingConnections(physicalConnection, assetDetailsArray, translate);
    if (drivingConnectionErrors?.length > 0) {
        globalErrors.push(...drivingConnectionErrors);
        hasValidationErrors = true;
    }

    // Validate each asset in the physical connection
    physicalConnection.forEach((asset, index) => {
        const assetDetails = assetDetailsArray.find(detail => detail?.assetId?.toString() === asset?.assetId?.toString());
        const validation = validateAsset(asset, assetDetails, outputRatios, translate);

        if (!validation.isValid) {
            hasValidationErrors = true;
        }

        // Add asset-specific errors to globalErrors for display in error banner
        if (validation?.errors?.length > 0) {
            validation.errors.forEach(error => {
                // Add asset name to the error message for clarity
                const assetName = asset?.name || asset?.assetId;
                const errorWithAssetContext = {
                    ...error,
                    message: `${assetName}: ${error?.message}`,
                    assetId: asset?.assetId
                };
                globalErrors.push(errorWithAssetContext);
            });
        }

        validationResults.push({
            assetId: asset.assetId,
            index,
            ...validation
        });
    });

    return {
        isValid: !hasValidationErrors && globalErrors.filter(e => e.severity === 'error')?.length === 0,
        hasErrors: hasValidationErrors || globalErrors.filter(e => e.severity === 'error')?.length > 0,
        hasWarnings: globalErrors.filter(e => e.severity === 'warning')?.length > 0 ||
            validationResults.some(result => result.warnings?.length > 0),
        globalErrors,
        assetValidations: validationResults,
        totalErrors: globalErrors.filter(e => e.severity === 'error')?.length +
            validationResults.reduce((count, result) => count + (result.errors?.length || 0), 0),
        totalWarnings: globalErrors.filter(e => e.severity === 'warning')?.length +
            validationResults.reduce((count, result) => count + (result.warnings?.length || 0), 0)
    };
};

/**
 * Gets validation status for a specific asset
 * @param {string} assetId - Asset ID to check
 * @param {Object} validationResult - Overall validation result
 * @returns {Object} Asset validation status
 */
export const getAssetValidationStatus = (assetId, validationResult) => {
    const assetValidation = validationResult.assetValidations.find(
        validation => validation.assetId.toString() === assetId.toString()
    );

    // Also check for driving connection errors where this asset is the driving asset
    const drivingErrorsForAsset = validationResult?.globalErrors.filter(error =>
        error?.drivingAssetId?.toString() === assetId.toString()
    );

    // Combine errors - only include asset validation errors (don't duplicate from globalErrors)
    const allErrorsForAsset = [
        ...assetValidation ? assetValidation.errors : [],
        ...drivingErrorsForAsset
    ];

    // Combine all warnings for this asset
    const allWarningsForAsset = [
        ...assetValidation ? assetValidation.warnings : [],
        ...drivingErrorsForAsset.filter(e => e.severity === 'warning')
    ];

    const hasErrors = allErrorsForAsset.filter(e => e.severity !== 'warning')?.length > 0;
    const hasWarnings = allWarningsForAsset?.length > 0;

    return {
        hasErrors,
        hasWarnings,
        errors: allErrorsForAsset,
        warnings: allWarningsForAsset,
        requiresOutputRatio: assetValidation?.requiresOutputRatio || false,
        outputRatio: assetValidation?.outputRatio || ''
    };
};
